import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';

import AssetRentRequests from '@components/routes/AdminPanel/AssetsRent/AssetRentRequests';

import { Icons } from '@icons/icons';

import { useQueryAssetRentRequest } from '@hooks/assets/useQueryAssetRentRequest';
import { useQueryAssetRentRequestStatuses } from '@hooks/assets/useQueryAssetRentRequestStatuses';
import { useQueryNetworkOperators } from '@hooks/common/useQueryNetworkOperators';

import { assetRentIncomingRequestsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSearchValue, setSort } from '@slices/assetsRentIncomingRequests';

import { getAssetRentIncomingRequests } from '@api/assetsRent';

import { isEmpty } from '@utils/common';

import { IPageSize } from '@interfaces/common/store';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITableSort, TTableFilter } from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { ASSETS_RENT_OUTGOING_REQUESTS } from '@constants/queryKeys';

import getIncomingRequestsColumns from './getIncomingRequestsColumns';

export const pageTitle = 'Incoming Requests';

const AssetRentIncomingRequests = () => {
	const dispatch = useAppDispatch();

	const [selectedNetworkOperator, setSelectedNetworkOperator] = useState<TSelectOption | null>(null);
	const [selectedRequestStatus, setSelectedRequestStatus] = useState<TSelectOption | null>(null);
	const [organisations, setOrganisations] = useState<TSelectOption[]>([]);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = useAppSelector(assetRentIncomingRequestsSelector);

	const {
		requestsStatuses,
		isLoadingRequestsStatuses,
		isFetchingRequestsStatuses,
	} = useQueryAssetRentRequestStatuses();

	const handleSetNetworkOperatorName = (name: TSelectOption): void => {
		setSelectedNetworkOperator(name);
	};

	const handleSetNetworkOperatorStatus = (status: TSelectOption): void => {
		setSelectedRequestStatus(status);
	};

	const isLoading = isLoadingRequestsStatuses || isFetchingRequestsStatuses;

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'networkOperatorId',
				placeholder: 'Organisation',
				option: selectedNetworkOperator,
				options: organisations,
				minWidth: '200px',
				setState: handleSetNetworkOperatorName,
				defaultValue: 'All',
			},
			{
				name: 'requestStatus',
				placeholder: 'Request Status',
				option: selectedRequestStatus,
				options: requestsStatuses,
				minWidth: '200px',
				setState: handleSetNetworkOperatorStatus,
				defaultValue: 'All',
			},
		];
	}, [selectedRequestStatus, requestsStatuses, selectedNetworkOperator, organisations]);

	const handleSetPage = useCallback((page: number): void => {
		dispatch(setPage({ page }));
	}, [dispatch]);

	const handleSetPageSize = (pageSize: number): PayloadAction<IPageSize> => dispatch(setPageSize({ pageSize }));

	const handleChangeSearchValue = useCallback((searchValue: string) => {
		handleSetPage(defaultMeta.page);
		dispatch(setSearchValue({ searchValue }));
	}, [dispatch, handleSetPage]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		if (isEmpty(sortBy)) return;

		dispatch(setSort({
			sortDirection: sortBy[0].desc,
			sortFieldName: sortBy[0].id.split('.')[0],
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{
				id: 'createdAt',
				desc: false,
			}],
			handleSort,
		};
	}, [handleSort]);

	const {
		requests,
		countOfRecords,
		isLoading: isUseRequestLoading,
	} = useQueryAssetRentRequest({
		filters,
		getRequestFunction: getAssetRentIncomingRequests,
		isAO: true,
		page,
		pageSize,
		queryKey: ASSETS_RENT_OUTGOING_REQUESTS,
		searchValue,
		sortBy: [{ id: sortFieldName, desc: !!sortDirection }],
	});

	const { networkOperators } = useQueryNetworkOperators(true);

	useEffect(() => {
		if (isEmpty(networkOperators)) return;

		setOrganisations(networkOperators);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [networkOperators?.length]);

	return (
		<AssetRentRequests
			countOfRecords={countOfRecords}
			filters={filters}
			getColumns={getIncomingRequestsColumns}
			handleSetPage={handleSetPage}
			handleSetPageSize={handleSetPageSize}
			isLoading={isLoading || isUseRequestLoading}
			page={page}
			pageSize={pageSize}
			pageTitle={pageTitle}
			pageTitleIcon={Icons.incomingRequestLight()}
			requests={requests}
			searchValue={searchValue}
			setSearchValue={handleChangeSearchValue}
			sort={sort}
		/>
	);
};

export default AssetRentIncomingRequests;
